import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ArrowLeft, ArrowRight } from "lucide-react";

import Input from "../../../components/Input";
import { UIButton } from "../../../components/ui";
import messages from "../../../assets/locale/messages";
import { AUTH_CONTEXT_TYPE, isClientResetPasswordFlowBasePath } from "../../../utils/authPortal";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import {
  isCompanyResetPasswordFlowBasePath,
  isAllowedPathWhileCompanyMustChangePassword,
} from "../../../utils/companyRegistration";
import { isAllowedPathWhileClientMustChangePassword } from "../../../utils/clientPortal";
import { generateDeviceId } from "../../../utils/Auth";
import { getFirebaseToken } from "../../../firebase/helpers";
import { loginRequest } from "../../../store/Auth/actions";

const renderSubtitleWithEmail = (template, email) => {
  if (!template.includes("{email}")) {
    return template;
  }
  const [before, after] = template.split("{email}");
  const displayEmail = email.trim() || "—";
  return (
    <>
      {before}
      <span className="auth-form-shell__email-accent">{displayEmail}</span>
      {after}
    </>
  );
};

const ResetPasswordStep1 = ({
  initialEmail,
  emailLocked,
  authPortal = AUTH_CONTEXT_TYPE.COMPANY,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const dispatch = useDispatch();
  const history = useHistory();
  const { resetPassword: t } = messages[lang];

  const resetPasswordPath =
    authPortal === AUTH_CONTEXT_TYPE.CLIENT
      ? ROUTE_PATHS.clientResetPassword
      : ROUTE_PATHS.companyResetPassword;

  const isResetPasswordFlowBasePath =
    authPortal === AUTH_CONTEXT_TYPE.CLIENT
      ? isClientResetPasswordFlowBasePath
      : isCompanyResetPasswordFlowBasePath;

  useEffect(() => {
    const unblock = history.block((nextLocation) => {
      if (isResetPasswordFlowBasePath(nextLocation.pathname)) {
        return;
      }
      if (
        authPortal === AUTH_CONTEXT_TYPE.CLIENT &&
        isAllowedPathWhileClientMustChangePassword(nextLocation.pathname)
      ) {
        return;
      }
      if (
        authPortal === AUTH_CONTEXT_TYPE.COMPANY &&
        isAllowedPathWhileCompanyMustChangePassword(nextLocation.pathname)
      ) {
        return;
      }
      return false;
    });
    return unblock;
  }, [history, authPortal, isResetPasswordFlowBasePath]);

  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    isValid,
    dirty,
  } = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      email: initialEmail || "",
      temporaryPassword: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("validEmailFormat")
        .required("emailRequired"),
      temporaryPassword: Yup.string().required("tempPasswordRequired"),
    }),
    onSubmit: (formValues) => {
      const email = String(formValues.email).trim();
      dispatch(
        loginRequest({
          data: {
            email,
            password: formValues.temporaryPassword,
            device_id: generateDeviceId(),
            fcm_token: getFirebaseToken(),
          },
          userType: "client",
          authPortal,
          loginContext: "companyResetPassword",
          failureRedirectPath: resetPasswordPath,
          failureRedirectState: { email },
        }),
      );
    },
  });

  const subtitleEmail = useMemo(
    () => (values.email || initialEmail || "").trim(),
    [values.email, initialEmail],
  );

  const NextIcon = lang === "ar" ? ArrowLeft : ArrowRight;

  return (
    <div className="reset-password-flow__step w-100">
      <header className="auth-form-shell__header">
        <h1 className="auth-layout__card__title">{t.step1.title}</h1>
        <p className="auth-layout__card__subtitle mb-0">
          {renderSubtitleWithEmail(t.step1.subtitle, subtitleEmail)}
        </p>
      </header>

      <form
        className="auth-form-shell__form"
        onSubmit={handleSubmit}
        noValidate
      >
        <Input
          id="reset-password-email"
          name="email"
          type="email"
          label={t.step1.email}
          placeholder={t.step1.emailPlaceholder}
          required
          value={values.email}
          onChange={(v) => {
            setFieldTouched("email", true);
            setFieldValue("email", v);
          }}
          onBlur={handleBlur}
          isInputHasErr={Boolean(touched.email && errors.email)}
          errMsg={errors.email}
          disabled={emailLocked}
          inputWrapperClass="mb-0"
          labelClassName="font-medium mb-2"
        />

        <Input
          id="reset-password-temp"
          name="temporaryPassword"
          type="password"
          label={t.step1.temporaryPassword}
          placeholder={t.step1.temporaryPasswordPlaceholder}
          required
          value={values.temporaryPassword}
          onChange={(v) => {
            setFieldTouched("temporaryPassword", true);
            setFieldValue("temporaryPassword", v);
          }}
          onBlur={handleBlur}
          isInputHasErr={Boolean(
            touched.temporaryPassword && errors.temporaryPassword,
          )}
          errMsg={errors.temporaryPassword}
          inputWrapperClass="mb-0"
          labelClassName="font-medium mb-2"
          isPasswordInput
        />

        <UIButton
          type="submit"
          variant="primary"
          size="large"
          fullWidth
          disabled={!(isValid && dirty)}
          endIcon={<NextIcon size={16} aria-hidden />}
        >
          {t.next}
        </UIButton>
      </form>
    </div>
  );
};

ResetPasswordStep1.propTypes = {
  initialEmail: PropTypes.string,
  emailLocked: PropTypes.bool,
  authPortal: PropTypes.oneOf(Object.values(AUTH_CONTEXT_TYPE)),
};

ResetPasswordStep1.defaultProps = {
  initialEmail: "",
  emailLocked: false,
};

export default ResetPasswordStep1;
